/**
 * Manual routine launch dialog (REQ-FRT-001 “Lanzamiento manual” / “Probar”).
 *
 * Sends the typed parameters as JSON and the `test` flag; the backend applies
 * the routine's input schema and returns the created run id/status.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '@/components/ui';
import { useI18n } from '@/i18n';
import { useLaunchRoutine } from '../hooks/useFrontier';
import type { FrontierRoutine } from '../types';

function errorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const candidate = detail as { message?: string; code?: string };
    if (candidate.message) return candidate.message;
    if (candidate.code) return candidate.code;
  }
  return fallback;
}

export interface LaunchRoutineDialogProps {
  routine: FrontierRoutine | null;
  onOpenChange: (open: boolean) => void;
}

export function LaunchRoutineDialog({ routine, onOpenChange }: LaunchRoutineDialogProps) {
  const { t } = useI18n();
  const launch = useLaunchRoutine();
  const [params, setParams] = useState('{}');
  const [test, setTest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [launched, setLaunched] = useState<string | null>(null);

  useEffect(() => {
    if (routine) {
      setParams('{}');
      setTest(false);
      setError(null);
      setLaunched(null);
    }
  }, [routine]);

  const handleLaunch = async () => {
    if (!routine) return;
    setError(null);
    setLaunched(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(params || '{}');
    } catch {
      setError(t('frontier.launch.invalidParams'));
      return;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      setError(t('frontier.launch.invalidParams'));
      return;
    }
    try {
      const result = await launch.mutateAsync({
        routineId: routine.id,
        payload: { params: parsed as Record<string, unknown>, test },
      });
      setLaunched(result.run_id);
    } catch (err) {
      setError(errorMessage(err, t('frontier.launch.error')));
    }
  };

  return (
    <Dialog open={!!routine} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('frontier.launch.title')}
            {routine ? ` · ${routine.name}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="launch-params">{t('frontier.launch.params')}</Label>
            <Textarea
              id="launch-params"
              data-testid="launch-params"
              rows={6}
              value={params}
              onChange={(e) => setParams(e.target.value)}
              className="font-mono text-xs"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="launch-test"
              checked={test}
              onChange={(e) => setTest(e.target.checked)}
            />
            {t('frontier.launch.test')}
          </label>
          {launched && (
            <p className="text-sm text-emerald-600" role="status">
              {t('frontier.launch.launched')}{' '}
              <Link
                className="underline"
                to={`/frontier/runs/${launched}`}
                data-testid="launch-open-run"
              >
                {t('frontier.launch.openRun')}
              </Link>
            </p>
          )}
          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={launch.isPending}
          >
            {t('frontier.editor.cancel')}
          </Button>
          <Button
            onClick={handleLaunch}
            disabled={launch.isPending}
            data-testid="launch-submit"
          >
            {launch.isPending ? t('frontier.launch.launching') : t('frontier.launch.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
